import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { ZugewieseneBaustelleUpdated } from "../components/eingangsrechnungen/BaustellenEintrag";

interface BaustellenEintragContextType {
  betragValues: Record<number, number>;
  updateBetrag: (index: number, value: number) => void;
  getTotalBetrag: () => number;
  sumAssignedNetto: number;
  initializeBetragValues: (entries: ZugewieseneBaustelleUpdated[]) => void;
}

const BaustellenEintragContext = createContext<
  BaustellenEintragContextType | undefined
>(undefined);

export const BaustellenEintragProvider = ({
  children,
  initialEntries,
}: {
  children: ReactNode;
  initialEntries?: any[];
}) => {
  const [betragValues, setBetragValues] = useState<Record<number, number>>({});
  const [sumAssignedNetto, setSumAssignedNetto] = useState<number>(0);

  const updateBetrag = useCallback((index: number, value: number) => {
    setBetragValues((prev) => ({
      ...prev,
      [index]: value,
    }));
  }, []);

  const initializeBetragValues = useCallback(
    (entries: ZugewieseneBaustelleUpdated[]) => {
      const initialValues: Record<number, number> = {};

      entries.forEach((entry, index) => {
        if (entry.betrag) {
          const formattedBetrag =
            typeof entry.betrag === "string"
              ? parseFloat(entry.betrag.replace(".", "").replace(",", "."))
              : parseFloat(entry.betrag);

          if (!isNaN(formattedBetrag)) {
            initialValues[index] = formattedBetrag;
          }
        }
      });

      setBetragValues(initialValues);
    },
    []
  );

  const getTotalBetrag = (): number => {
    return Object.values(betragValues).reduce((sum, value) => sum + value, 0);
  };

  useEffect(() => {
    setSumAssignedNetto(getTotalBetrag());
  }, [betragValues]);

  useEffect(() => {
    if (initialEntries && initialEntries.length > 0) {
      initializeBetragValues(initialEntries);
    }
  }, [initialEntries, initializeBetragValues]);

  return (
    <BaustellenEintragContext.Provider
      value={{
        betragValues,
        updateBetrag,
        getTotalBetrag,
        sumAssignedNetto,
        initializeBetragValues,
      }}
    >
      {children}
    </BaustellenEintragContext.Provider>
  );
};

export const useBaustellenEintragContext = (): BaustellenEintragContextType => {
  const context = useContext(BaustellenEintragContext);
  if (!context) {
    throw new Error(
      "useBaustellenEintragContext must be used within a BaustellenEintragProvider"
    );
  }
  return context;
};
